import { Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { ScheduleCallCTA } from "@/components/marketing/ScheduleCallCTA";
import { PublicNav } from "@/components/PublicNav";
import { Button } from "@/components/ui/button";
import { FeatureShowcase } from "@/components/marketing/FeatureShowcase";
import { motion } from "framer-motion";
import { ROUTES } from '@/lib/routes';

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* Hero — tight, punchy, above-the-fold */}
      <section className="relative overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          src="/videos/hero-bg.mp4"
        />
        <div className="absolute inset-0 bg-background/20" />
        <div className="absolute inset-0 shadow-[inset_0_0_60px_30px_rgba(0,0,0,0.8)]" />
        <div className="container mx-auto px-4 pt-28 pb-16 md:pt-40 md:pb-24 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center space-y-6"
          >
            <div className="inline-block px-4 py-2 rounded-full bg-black/50 text-white text-sm font-semibold backdrop-blur-sm">
              Club Operating System · Performance Intelligence
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Run your club. Know your squad.
            </h1>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/get-started">
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary-glow hover:opacity-90 transition-opacity shadow-lg">
                  Get Started
                </Button>
              </Link>
              <Link to={ROUTES.PRICING}>
                <Button size="lg" variant="outline">
                  View Pricing
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tagline */}
      <div className="text-center py-10">
        <p className="text-lg md:text-xl font-bold leading-relaxed max-w-2xl mx-auto bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
          Players, processes, performance, and AI-driven decisions — all in one place. Built to scale with your ambition.
        </p>
      </div>

      {/* Feature Rotator — DropATask-style tabbed showcase */}
      <FeatureShowcase />

      {/* Two-track value — concise */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="rounded-xl border border-primary/20 bg-card p-8 space-y-4">
              <div className="inline-block px-2 py-1 rounded bg-primary/10 text-primary text-xs font-semibold">
                Foundation
              </div>
              <h3 className="text-xl font-bold">Club OS for smaller clubs</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Members, squads, messaging, finances, compliance, and scheduling — replace spreadsheets and WhatsApp groups.
              </p>
            </div>

            <div className="rounded-xl border border-secondary/20 bg-card p-8 space-y-4">
              <div className="inline-block px-2 py-1 rounded bg-secondary/10 text-secondary text-xs font-semibold">
                Performance Core → Enterprise
              </div>
              <h3 className="text-xl font-bold">Decision intelligence for serious clubs</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Readiness forecasting, AI briefings, injury guardrails, scouting pipelines, and audit-ready governance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Call CTA */}
      <ScheduleCallCTA />

      {/* Footer */}
      <footer className="border-t border-border py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Activity className="h-6 w-6 text-primary" />
                <span className="text-lg font-bold">AlphaSignal</span>
              </div>
              <p className="text-sm text-muted-foreground">
                The club operating system and performance intelligence platform for football.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/marketing" className="hover:text-foreground transition-colors">Platform</Link></li>
                <li><Link to={ROUTES.PRICING} className="hover:text-foreground transition-colors">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/marketing#contact" className="hover:text-foreground transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/legal/privacy" className="hover:text-foreground transition-colors">Privacy</Link></li>
                <li><Link to="/legal/terms" className="hover:text-foreground transition-colors">Terms</Link></li>
                <li><Link to="/legal/security" className="hover:text-foreground transition-colors">Security</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            © 2026 AlphaSignal. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
